import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Common currencies — kept short so the dropdown stays usable. Users can add
// more by typing any ISO-4217 code into any numerical field; the backend
// accepts any string.
const COMMON_CURRENCIES = ["KYD", "USD", "EUR", "GBP", "CAD", "AUD", "BSD", "JMD", "MXN", "CNY", "JPY"];

/**
 * CurrencyRateInput — three-control row: [Currency select][FX rate][Refresh].
 * Hides automatically when `currency === baseCurrency` and locks fx_rate=1.
 *
 * Props:
 *   baseCurrency  — the org's base currency (from currentOrg.currency)
 *   currency      — currently selected ISO code (controlled)
 *   fxRate        — current fx_rate (controlled, Number or "")
 *   amount        — total amount in document currency (for live equivalent preview)
 *   onCurrency    — (ccy) => void
 *   onFxRate      — (rate) => void
 *   testId        — base data-testid prefix
 */
export default function CurrencyRateInput({
    baseCurrency = "KYD",
    currency,
    fxRate,
    amount = 0,
    onCurrency,
    onFxRate,
    testId = "fx",
}) {
    const [fetching, setFetching] = useState(false);
    const isForeign = currency && currency !== baseCurrency;

    // Auto-fetch the current rate when currency changes to a non-base code and
    // fx_rate is empty or still the default "1". User can always override the
    // value manually after. We include `1` in the "unset" check so that the
    // first switch from base->foreign populates a real rate instead of leaving
    // the stale "1" behind.
    useEffect(() => {
        if (!isForeign) {
            if (fxRate !== 1) onFxRate?.(1);
            return;
        }
        if (fxRate == null || fxRate === "" || Number(fxRate) === 1) fetchRate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency, baseCurrency]);

    const fetchRate = async () => {
        if (!isForeign) return;
        setFetching(true);
        try {
            const { data } = await api.get(`/fx/rate?base=${currency}&target=${baseCurrency}`);
            onFxRate?.(Number(data.rate.toFixed(6)));
        } catch (e) {
            toast.error(`Couldn't fetch live FX rate — enter manually`);
        } finally {
            setFetching(false);
        }
    };

    const equivalent = (Number(amount) || 0) * (Number(fxRate) || 1);

    return (
        <div className="rounded-2xl border border-sand-200 bg-sand-50/50 p-3 space-y-2" data-testid={`${testId}-root`}>
            <div className="flex items-end gap-2">
                <div className="w-28">
                    <Label className="text-xs">Currency</Label>
                    <Select value={currency || baseCurrency} onValueChange={onCurrency}>
                        <SelectTrigger className="mt-1" data-testid={`${testId}-currency`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from(new Set([baseCurrency, ...COMMON_CURRENCIES])).map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {isForeign && (
                    <>
                        <div className="flex-1">
                            <Label className="text-xs">1 {currency} = ? {baseCurrency}</Label>
                            <Input
                                type="number"
                                step="0.000001"
                                value={fxRate ?? ""}
                                onChange={(e) => onFxRate?.(e.target.value === "" ? "" : Number(e.target.value))}
                                className="mt-1 font-mono"
                                data-testid={`${testId}-rate`}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={fetchRate}
                            className="h-10 px-3 rounded-xl bg-white border border-sand-300 hover:bg-sand-100 text-ocean-700 text-sm inline-flex items-center gap-1.5"
                            title="Fetch live rate"
                            data-testid={`${testId}-refresh`}
                        >
                            {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </button>
                    </>
                )}
            </div>
            {isForeign && (
                <div className="text-xs text-muted-foreground" data-testid={`${testId}-equivalent`}>
                    Equivalent: <span className="font-mono text-ocean-700 font-semibold">
                        {baseCurrency} {equivalent.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                    </span>
                    {" · "}The stored rate will be used for reporting and cannot be changed after save.
                </div>
            )}
        </div>
    );
}
